import React, { useState, useEffect, useCallback } from 'react';
import {
  Save, Lock, Eye, EyeOff, Globe, Clock,
  Copy, RefreshCw, Wifi, WifiOff, Check, Code, ChevronDown, ChevronUp,
  Key, Radio, Plus, Trash2, Server, Cpu, Shield, Monitor, Link, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import {
  getEspSettings, updateEspSettings, regenerateEspKey, getEspStatus, changePassword,
  getMqttSettings, updateMqttSettings, getMqttStatus,
  getEspDevices, createEspDevice, deleteEspDevice, regenerateDeviceKey,
  getSiteSettings, updateSiteSettings
} from '@/lib/api';
import usePolling from '@/hooks/usePolling';
import { applyToDOM } from '@/hooks/useSiteSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIMEZONES = [
  { offset: -12, label: 'UTC-12 (Baker, Howland)' }, { offset: -11, label: 'UTC-11 (Samoa)' },
  { offset: -10, label: 'UTC-10 (Hawaii)' },         { offset: -9,  label: 'UTC-9 (Alaska)' },
  { offset: -8,  label: 'UTC-8 (Los Angeles)' },     { offset: -7,  label: 'UTC-7 (Denver)' },
  { offset: -6,  label: 'UTC-6 (Chicago)' },         { offset: -5,  label: 'UTC-5 (New York)' },
  { offset: -4,  label: 'UTC-4 (Halifax)' },         { offset: -3,  label: 'UTC-3 (Buenos Aires)' },
  { offset: -2,  label: 'UTC-2 (Orta Atlantik)' },   { offset: -1,  label: 'UTC-1 (Azor)' },
  { offset: 0,   label: 'UTC+0 (Londra, GMT)' },     { offset: 1,   label: 'UTC+1 (Paris, Berlin)' },
  { offset: 2,   label: 'UTC+2 (Atina, Kahire)' },   { offset: 3,   label: 'UTC+3 (İstanbul, Moskova) ★' },
  { offset: 4,   label: 'UTC+4 (Dubai, Bakü)' },     { offset: 5,   label: 'UTC+5 (Karaçi)' },
  { offset: 6,   label: 'UTC+6 (Almatı)' },          { offset: 7,   label: 'UTC+7 (Bangkok)' },
  { offset: 8,   label: 'UTC+8 (Pekin, Singapur)' }, { offset: 9,   label: 'UTC+9 (Tokyo, Seul)' },
  { offset: 10,  label: 'UTC+10 (Sydney)' },         { offset: 11,  label: 'UTC+11 (Honiara)' },
  { offset: 12,  label: 'UTC+12 (Auckland)' },
];

const useLocalTime = (offset) => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const local = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + offset * 3600000);
      setTime(local.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [offset]);
  return time;
};

const arduinoCode = (key) => `/*
 * Smart Office Hub — ESP8266 v2.1 (Bellek Optimizeli)
 * Global SSL client kullanır — her request'te yeni nesne YARATMAZ
 */
#include <ESP8266WiFi.h>
#include <ESP8266HTTPClient.h>
#include <WiFiClientSecureBearSSL.h>
#include <ArduinoJson.h>

const char* SSID     = "WIFI_AGINIZ";
const char* PASSWORD = "WIFI_SIFRENIZ";
const char* BACKEND  = "${BACKEND_URL}";
const char* API_KEY  = "${key}";

const unsigned long POLL_MS = 3000;   // 3 saniye (1s değil — bellek tasarrufu)
const unsigned long HB_MS   = 10000;  // 10 saniye heartbeat

// ÖNEMLİ: Global SSL client — TEK KERE oluşturuluyor!
BearSSL::WiFiClientSecure sslClient;

// Shift Register pinleri (74HC595)
#define DATA_PIN  13  // D7
#define CLOCK_PIN 14  // D5
#define LATCH_PIN 12  // D6
#define ACTIVE_LOW true

bool relayStates[16] = {false};
unsigned long lastPoll = 0, lastHB = 0;
bool firstPoll = true;

void applyRelays() {
  digitalWrite(LATCH_PIN, LOW);
  uint8_t b2=0, b1=0;
  for(int i=8;i<16;i++) if(ACTIVE_LOW?!relayStates[i]:relayStates[i]) b2|=(1<<(i-8));
  for(int i=0;i<8;i++)  if(ACTIVE_LOW?!relayStates[i]:relayStates[i]) b1|=(1<<i);
  shiftOut(DATA_PIN,CLOCK_PIN,MSBFIRST,b2);
  shiftOut(DATA_PIN,CLOCK_PIN,MSBFIRST,b1);
  digitalWrite(LATCH_PIN, HIGH);
}

void connectWiFi() {
  WiFi.begin(SSID, PASSWORD);
  while(WiFi.status()!=WL_CONNECTED){delay(500);Serial.print(".");}
  Serial.println("\\n[WiFi] IP: "+WiFi.localIP().toString());
}

bool doPoll() {
  HTTPClient https;
  if(!https.begin(sslClient, String(BACKEND)+"/api/esp/poll?key="+API_KEY)) return false;
  https.setTimeout(8000);
  int code = https.GET();
  if(code!=200){ https.end(); return false; }
  StaticJsonDocument<1024> doc;
  if(deserializeJson(doc,https.getString())){ https.end(); return false; }
  https.end();
  for(JsonObject r : doc["relays"].as<JsonArray>()){
    int n=r["relay_number"]; bool s=r["state"];
    if(n>=1&&n<=16&&(relayStates[n-1]!=s||firstPoll)){ relayStates[n-1]=s; }
  }
  applyRelays();
  if(firstPoll){ Serial.println("[POLL] Baglandi!"); firstPoll=false; }
  if((++pollCount)%30==0)
    Serial.printf("[DURUM] Heap:%d Poll:%d\\n",ESP.getFreeHeap(),pollCount);
  return true;
}
int pollCount=0;

void doHeartbeat() {
  HTTPClient https;
  if(!https.begin(sslClient, String(BACKEND)+"/api/esp/heartbeat?key="+API_KEY)) return;
  https.addHeader("Content-Type","application/json");
  https.POST("{\\"ip\\":\\""+WiFi.localIP().toString()+"\\""+",\\"uptime\\":"+String(millis()/1000)+"}");
  https.end();
  Serial.printf("[HB] Heap:%d Sure:%lus\\n",ESP.getFreeHeap(),millis()/1000);
}

void setup() {
  Serial.begin(115200);
  pinMode(DATA_PIN,OUTPUT); pinMode(CLOCK_PIN,OUTPUT); pinMode(LATCH_PIN,OUTPUT);
  applyRelays();
  sslClient.setInsecure();
  sslClient.setBufferSizes(1024, 1024);  // Bellek optimizasyonu
  connectWiFi();
  configTime(3*3600,0,"pool.ntp.org");
  time_t t=time(nullptr); int n=0;
  while(t<100000&&n++<20){delay(500);t=time(nullptr);}
  Serial.printf("[HAZIR] Heap:%d\\n",ESP.getFreeHeap());
}

void loop() {
  if(WiFi.status()!=WL_CONNECTED){ connectWiFi(); return; }
  if(ESP.getFreeHeap()<8000){ Serial.println("[UYARI] Heap kritik!"); ESP.restart(); }
  unsigned long now=millis();
  if(now-lastPoll>=POLL_MS){ lastPoll=now; doPoll(); }
  if(now-lastHB>=HB_MS){ lastHB=now; doHeartbeat(); }
}`;

// ─── Shared Components ────────────────────────────────────────────────────────

const SectionTitle = ({ icon: Icon, title, subtitle, rightSlot }) => (
  <div className="flex items-start justify-between mb-5">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={17} className="text-primary" />
      </div>
      <div>
        <p className="font-semibold">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {rightSlot}
  </div>
);

const CopyButton = ({ text, id, copied, onCopy }) => (
  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => onCopy(text, id)}>
    {copied === id ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
  </Button>
);

// ─── ESP8266 Tab ──────────────────────────────────────────────────────────────

const EspTab = ({ settings, espDevices, setSettings, setEspDevices, loading }) => {
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newDev, setNewDev] = useState({ name: '', description: '', zone: '', relay_numbers: [] });
  const [addingDev, setAddingDev] = useState(false);
  const [copiedId, setCopiedId] = useState('');

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id); setTimeout(() => setCopiedId(''), 2000);
    toast.success('Kopyalandı');
  };

  const handleSaveTz = async () => {
    setSaving(true);
    try { await updateEspSettings(settings); toast.success('Kaydedildi'); }
    catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">

      {/* ESP Devices List */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="esp-devices-card">
        <SectionTitle icon={Cpu} title="ESP8266 Cihazları"
          subtitle="Her cihazın bağlantı durumu, API anahtarı ve röle grubu"
          rightSlot={
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" className="gap-1.5" data-testid="add-esp-device-btn">
                  <Plus size={13} /> Cihaz Ekle
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Yeni ESP8266 Cihaz</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="space-y-1.5">
                    <Label>Cihaz Adı *</Label>
                    <Input value={newDev.name} onChange={e => setNewDev(d => ({...d, name: e.target.value}))} placeholder="Ana Bina ESP" data-testid="device-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Bölge / Konum</Label>
                    <Input value={newDev.zone} onChange={e => setNewDev(d => ({...d, zone: e.target.value}))} placeholder="Zemin Kat" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Röle Sayısı <span className="text-muted-foreground font-normal">(1-16, boş = tümü)</span></Label>
                    <Input type="number" min="1" max="16"
                      value={newDev.relay_numbers.length || ''}
                      onChange={e => {
                        const n = parseInt(e.target.value);
                        setNewDev(d => ({
                          ...d,
                          relay_numbers: (!e.target.value || isNaN(n)) ? [] : Array.from({length: Math.min(n, 16)}, (_, i) => i + 1)
                        }));
                      }}
                      placeholder="Örn: 8 → Röle 1-8 atanır"
                    />
                    {newDev.relay_numbers.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Atanacak: <span className="font-mono-tech text-foreground">R1 – R{newDev.relay_numbers.length}</span> ({newDev.relay_numbers.length} röle)
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setAddOpen(false)}>İptal</Button>
                  <Button disabled={addingDev} data-testid="save-esp-device-btn" onClick={async () => {
                    if (!newDev.name.trim()) { toast.error('Cihaz adı giriniz'); return; }
                    setAddingDev(true);
                    try {
                      const r = await createEspDevice(newDev);
                      setEspDevices(prev => [...prev, r.data]);
                      setAddOpen(false);
                      setNewDev({ name: '', description: '', zone: '', relay_numbers: [] });
                      toast.success('Cihaz eklendi');
                    } catch { toast.error('Eklenemedi'); }
                    finally { setAddingDev(false); }
                  }}>{addingDev ? 'Ekleniyor...' : 'Ekle'}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          }
        />

        {loading ? (
          <div className="space-y-3">{Array(2).fill(0).map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}</div>
        ) : espDevices.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-border rounded-xl text-muted-foreground text-sm">
            <Cpu size={32} className="mx-auto mb-2 opacity-20" />
            <p>Henüz cihaz eklenmemiş.</p>
            <p className="text-xs mt-1">"Cihaz Ekle" butonuna tıklayarak ESP8266 cihazlarınızı kaydedin.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {espDevices.map(d => (
              <div key={d.id} className={`border rounded-2xl overflow-hidden transition-all ${d.online ? 'border-green-500/30' : 'border-border'}`} data-testid={`esp-device-${d.id}`}>

                {/* ── Header ── */}
                <div className={`flex items-center gap-3 px-4 py-3 ${d.online ? 'bg-green-500/5' : 'bg-muted/30'}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${d.online ? 'bg-green-500/15' : 'bg-muted'}`}>
                    <Server size={16} className={d.online ? 'text-green-500' : 'text-muted-foreground'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm">{d.name}</p>
                      {d.zone && <Badge variant="outline" className="text-xs">{d.zone}</Badge>}
                      {d.relay_numbers?.length > 0 && (
                        <Badge variant="outline" className="text-xs font-mono-tech">R1–R{d.relay_numbers.length}</Badge>
                      )}
                    </div>
                  </div>
                  <Badge className={`gap-1.5 shrink-0 ${d.online ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-muted text-muted-foreground border-border'}`} data-testid={`device-status-${d.id}`}>
                    {d.online ? <><Wifi size={11} />Çevrimiçi</> : <><WifiOff size={11} />Çevrimdışı</>}
                  </Badge>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Anahtarı Yenile"
                      onClick={async () => {
                        const r = await regenerateDeviceKey(d.id);
                        setEspDevices(prev => prev.map(x => x.id === d.id ? {...x, api_key: r.data.api_key} : x));
                        toast.success('Anahtar yenilendi');
                      }}>
                      <RefreshCw size={12} />
                    </Button>
                    <ConfirmDeleteDialog
                      title="Cihazı Sil"
                      description={`"${d.name}" cihazını silmek istediğinizden emin misiniz?`}
                      onConfirm={async () => {
                        await deleteEspDevice(d.id);
                        setEspDevices(prev => prev.filter(x => x.id !== d.id));
                        toast.success('Cihaz silindi');
                      }}
                      trigger={<Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"><Trash2 size={12} /></Button>}
                    />
                  </div>
                </div>

                {/* ── Bağlantı Durumu ── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border/40">
                  {[
                    { label: 'IP Adresi',     value: d.ip || '—',                                        mono: true  },
                    { label: 'Son Görülme',   value: d.last_seen ? new Date(d.last_seen).toLocaleTimeString('tr-TR') : 'Bağlanmadı' },
                    { label: 'Çalışma Süresi', value: (() => {
                      if (!d.uptime) return '—';
                      const h = Math.floor(d.uptime / 3600);
                      const m = Math.floor((d.uptime % 3600) / 60);
                      const s = d.uptime % 60;
                      if (h > 0) return `${h} sa ${m} dk`;
                      if (m > 0) return `${m} dk ${s} sn`;
                      return `${s} sn`;
                    })() },
                    { label: 'Durum',          value: d.online ? 'Aktif ✓' : 'Bekleniyor…',             green: d.online },
                  ].map(({ label, value, mono, green }) => (
                    <div key={label} className="bg-card px-3 py-2.5">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
                      <p className={`text-sm font-medium mt-0.5 ${mono ? 'font-mono-tech' : ''} ${green ? 'text-green-600 dark:text-green-400' : ''}`}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* ── API Anahtarı ── */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-t border-border/50">
                  <Key size={12} className="text-muted-foreground shrink-0" />
                  <p className="text-xs font-mono-tech text-muted-foreground flex-1 truncate">{d.api_key}</p>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                    onClick={() => copy(d.api_key, `key-${d.id}`)}>
                    {copiedId === `key-${d.id}` ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
                  </Button>
                </div>

                {/* ── Poll URL ── */}
                <div className="flex items-center gap-2 px-4 py-2.5 border-t border-border/50 bg-muted/20">
                  <Radio size={12} className="text-muted-foreground shrink-0" />
                  <p className="text-[10px] font-mono-tech text-muted-foreground flex-1 truncate">
                    {BACKEND_URL}/api/esp/poll?key={d.api_key}
                  </p>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0"
                    onClick={() => copy(`${BACKEND_URL}/api/esp/poll?key=${d.api_key}`, `url-${d.id}`)}>
                    {copiedId === `url-${d.id}` ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
                  </Button>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* Timezone */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="timezone-settings-card">
        <SectionTitle icon={Globe} title="Saat Dilimi" subtitle="Zamanlayıcı için yerel saat (UTC ofseti)" />
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1.5">
            <Select value={String(settings.timezone_offset)} onValueChange={v => setSettings(s => ({...s, timezone_offset: parseInt(v)}))}>
              <SelectTrigger data-testid="timezone-select">
                <SelectValue>{TIMEZONES.find(t => t.offset === settings.timezone_offset)?.label}</SelectValue>
              </SelectTrigger>
              <SelectContent>{TIMEZONES.map(tz => <SelectItem key={tz.offset} value={String(tz.offset)}>{tz.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="bg-muted rounded-xl px-4 py-2 text-center shrink-0">
            <p className="text-[10px] text-muted-foreground">Yerel saat</p>
            <p className="text-lg font-bold font-mono-tech leading-tight"><LocalClock offset={settings.timezone_offset} /></p>
          </div>
          <Button onClick={handleSaveTz} disabled={saving} className="gap-1.5 shrink-0" data-testid="save-settings-btn">
            <Save size={14} />{saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const LocalClock = ({ offset }) => {
  const time = useLocalTime(offset);
  return <>{time}</>;
};

// ─── MQTT Tab ──────────────────────────────────────────────────────────────────

const MqttTab = ({ mqtt, setMqtt, mqttStatus, loading }) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try { await updateMqttSettings(mqtt); toast.success('MQTT ayarları kaydedildi'); }
    catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      {/* Status banner */}
      <div className={`rounded-xl p-4 flex items-center gap-3 border ${mqttStatus.connected ? 'bg-green-500/5 border-green-500/30' : 'bg-muted border-border'}`} data-testid="mqtt-status-card">
        <Radio size={20} className={mqttStatus.connected ? 'text-green-500' : 'text-muted-foreground'} />
        <div>
          <p className="font-medium text-sm">{mqttStatus.connected ? 'MQTT Bağlantısı Aktif' : 'MQTT Bağlı Değil'}</p>
          <p className="text-xs text-muted-foreground">{mqttStatus.connected ? `Broker: ${mqtt.broker}` : 'Etkinleştirmek için ayarları doldurun ve kaydedin'}</p>
        </div>
        <Badge className={`ml-auto gap-1.5 ${mqttStatus.connected ? 'bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/30' : 'bg-muted text-muted-foreground'}`} data-testid="mqtt-status-badge">
          {mqttStatus.connected ? <><Wifi size={12} /> Bağlı</> : <><WifiOff size={12} /> Bağlı Değil</>}
        </Badge>
      </div>

      <div className="bg-card border border-border rounded-xl p-5" data-testid="mqtt-settings-card">
        {loading ? (
          <div className="space-y-3">{Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
        ) : (
          <div className="space-y-5">
            {/* Enable toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-muted">
              <div>
                <p className="font-medium text-sm">MQTT Aktif</p>
                <p className="text-xs text-muted-foreground">Röle toggle'larında broker'a publish gönderilir</p>
              </div>
              <Switch checked={mqtt.enabled} onCheckedChange={v => setMqtt(m => ({...m, enabled: v}))} data-testid="mqtt-enabled-toggle" />
            </div>

            <Separator />

            {/* Broker settings */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Broker Adresi</Label>
                <Input value={mqtt.broker} onChange={e => setMqtt(m => ({...m, broker: e.target.value}))} placeholder="test.mosquitto.org" className="font-mono-tech" data-testid="mqtt-broker-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Port</Label>
                <Input type="number" value={mqtt.port} onChange={e => setMqtt(m => ({...m, port: parseInt(e.target.value)||1883}))} className="font-mono-tech" data-testid="mqtt-port-input" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Kullanıcı Adı <span className="text-muted-foreground font-normal">(opsiyonel)</span></Label>
                <Input value={mqtt.username} onChange={e => setMqtt(m => ({...m, username: e.target.value}))} placeholder="mqtt_user" />
              </div>
              <div className="space-y-1.5">
                <Label>Şifre <span className="text-muted-foreground font-normal">(opsiyonel)</span></Label>
                <Input type="password" value={mqtt.password} onChange={e => setMqtt(m => ({...m, password: e.target.value}))} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Topic Prefix</Label>
              <Input value={mqtt.topic_prefix} onChange={e => setMqtt(m => ({...m, topic_prefix: e.target.value}))} className="font-mono-tech" data-testid="mqtt-topic-input" />
            </div>

            {/* Topic preview */}
            <div className="bg-muted rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Topic Önizleme</p>
              <div className="grid grid-cols-2 gap-3 text-xs font-mono-tech">
                <div>
                  <p className="text-muted-foreground mb-1">Backend → ESP (Publish)</p>
                  <p className="bg-background rounded-lg px-2 py-1.5 text-foreground">{mqtt.topic_prefix}/relay/1 → <span className="text-green-500">on</span></p>
                </div>
                <div>
                  <p className="text-muted-foreground mb-1">ESP Subscribe Konusu</p>
                  <p className="bg-background rounded-lg px-2 py-1.5 text-foreground">{mqtt.topic_prefix}/relay/+</p>
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full gap-2" data-testid="save-mqtt-btn">
              <Save size={14} />{saving ? 'Kaydediliyor...' : 'MQTT Ayarlarını Kaydet'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Security Tab ─────────────────────────────────────────────────────────────

const SecurityTab = () => {
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, next: false });

  const handleSave = async () => {
    if (!pwForm.current) { toast.error('Mevcut şifreyi girin'); return; }
    if (pwForm.next.length < 4) { toast.error('Yeni şifre en az 4 karakter olmalı'); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error('Şifreler eşleşmiyor'); return; }
    setPwSaving(true);
    try {
      await changePassword(pwForm.current, pwForm.next);
      toast.success('Şifre güncellendi');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Şifre değiştirilemedi'); }
    finally { setPwSaving(false); }
  };

  const PwField = ({ id, label, value, stateKey, showKey }) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="relative">
        <Input id={id} type={showPw[showKey] ? 'text' : 'password'} value={value}
          onChange={e => setPwForm(f => ({...f, [stateKey]: e.target.value}))}
          placeholder="••••••••" className="pr-10" data-testid={`${id}-input`} />
        <button type="button" onClick={() => setShowPw(s => ({...s, [showKey]: !s[showKey]}))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
          {showPw[showKey] ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-md">
      <div className="bg-card border border-border rounded-xl p-5" data-testid="password-settings-card">
        <SectionTitle icon={Shield} title="Şifre Değiştir" subtitle="Hesap güvenliği" />
        <div className="space-y-4">
          <PwField id="current-password" label="Mevcut Şifre" value={pwForm.current} stateKey="current" showKey="current" />
          <PwField id="new-password" label="Yeni Şifre" value={pwForm.next} stateKey="next" showKey="next" />
          <div className="space-y-1.5">
            <Label>Yeni Şifre (Tekrar)</Label>
            <Input type="password" value={pwForm.confirm}
              onChange={e => setPwForm(f => ({...f, confirm: e.target.value}))}
              placeholder="••••••••"
              className={pwForm.confirm && pwForm.next !== pwForm.confirm ? 'border-destructive' : ''}
              data-testid="confirm-password-input" />
            {pwForm.confirm && pwForm.next !== pwForm.confirm && (
              <p className="text-xs text-destructive">Şifreler eşleşmiyor</p>
            )}
          </div>
          <Button onClick={handleSave} disabled={pwSaving} className="w-full gap-2 mt-2" data-testid="change-password-btn">
            <Lock size={14} />{pwSaving ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Site Tab ─────────────────────────────────────────────────────────────────

const SiteTab = () => {
  const [site, setSite] = useState({
    site_title: 'Smart Office Hub',
    site_description: 'Akıllı Ofis Otomasyon Sistemi',
    favicon_url: '/favicon.ico',
    theme_color: '#2563EB',
    logo_url: '',
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSiteSettings()
      .then(r => setSite(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSiteSettings(site);
      applyToDOM(site); // Anlık uygula
      toast.success('Site ayarları kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const PRESET_COLORS = ['#2563EB','#7C3AED','#059669','#DC2626','#D97706','#0891B2','#1D4ED8','#374151'];

  return (
    <div className="max-w-xl space-y-5">
      <div className="bg-card border border-border rounded-xl p-5" data-testid="site-settings-card">
        <SectionTitle icon={Monitor} title="Site Bilgileri" subtitle="Tarayıcı sekmesi ve meta etiketleri" />

        {loading ? <div className="space-y-3">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div> : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Site Başlığı</Label>
              <Input value={site.site_title}
                onChange={e => setSite(s => ({...s, site_title: e.target.value}))}
                placeholder="Smart Office Hub"
                data-testid="site-title-input" />
              <p className="text-xs text-muted-foreground">Tarayıcı sekmesinde ve PWA simgesinde görünür</p>
            </div>

            <div className="space-y-1.5">
              <Label>Açıklama</Label>
              <Input value={site.site_description}
                onChange={e => setSite(s => ({...s, site_description: e.target.value}))}
                placeholder="Akıllı Ofis Otomasyon Sistemi"
                data-testid="site-description-input" />
              <p className="text-xs text-muted-foreground">Arama motorlarında ve paylaşımlarda görünür</p>
            </div>

            <Separator />

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Link size={13} className="text-primary" /> Favicon URL</Label>
              <div className="flex gap-2">
                <Input value={site.favicon_url}
                  onChange={e => setSite(s => ({...s, favicon_url: e.target.value}))}
                  placeholder="/favicon.ico veya https://..."
                  className="font-mono-tech flex-1"
                  data-testid="favicon-url-input" />
                {site.favicon_url && (
                  <div className="w-10 h-10 border border-border rounded-lg flex items-center justify-center shrink-0 bg-muted">
                    <img src={site.favicon_url} alt="favicon" className="w-6 h-6 object-contain"
                      onError={e => { e.target.style.display='none'; }} />
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">.ico, .png veya .svg formatında URL giriniz</p>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Link size={13} className="text-primary" /> Logo URL (Sidebar)</Label>
              <Input value={site.logo_url}
                onChange={e => setSite(s => ({...s, logo_url: e.target.value}))}
                placeholder="https://ornek.com/logo.png (boş = varsayılan ikon)"
                className="font-mono-tech"
                data-testid="logo-url-input" />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Palette size={13} className="text-primary" /> Tema Rengi</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={site.theme_color}
                  onChange={e => setSite(s => ({...s, theme_color: e.target.value}))}
                  className="w-10 h-10 rounded-lg border border-border cursor-pointer"
                  data-testid="theme-color-input" />
                <Input value={site.theme_color}
                  onChange={e => setSite(s => ({...s, theme_color: e.target.value}))}
                  className="font-mono-tech w-32" placeholder="#2563EB" />
                <div className="flex gap-1.5 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setSite(s => ({...s, theme_color: c}))}
                      className={`w-6 h-6 rounded-lg transition-all border-2 ${site.theme_color === c ? 'border-foreground scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} title={c} />
                  ))}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Mobilde adres çubuğu rengi ve PWA tema rengi</p>
            </div>

            {/* Preview */}
            <div className="bg-muted rounded-xl p-4 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ backgroundColor: site.theme_color }}>
                {site.logo_url
                  ? <img src={site.logo_url} className="w-6 h-6 object-contain" onError={e => { e.target.style.display='none'; }} alt="" />
                  : site.site_title?.[0]?.toUpperCase() || 'S'}
              </div>
              <div>
                <p className="text-sm font-semibold">{site.site_title || 'Site Başlığı'}</p>
                <p className="text-xs text-muted-foreground">{site.site_description || 'Açıklama'}</p>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full gap-2" data-testid="save-site-btn">
              <Save size={14} />{saving ? 'Kaydediliyor...' : 'Site Ayarlarını Kaydet'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Settings Page ───────────────────────────────────────────────────────

const Settings = () => {
  const [settings, setSettings] = useState({ esp_api_key: '', timezone_offset: 3, poll_interval_ms: 1000 });
  const [espStatus, setEspStatus] = useState(null);
  const [espDevices, setEspDevices] = useState([]);
  const [mqtt, setMqtt] = useState({ enabled: false, broker: 'test.mosquitto.org', port: 1883, username: '', password: '', topic_prefix: 'smartoffice' });
  const [mqttStatus, setMqttStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const [espS, mqttS, devs] = await Promise.all([getEspStatus(), getMqttStatus(), getEspDevices()]);
      setEspStatus(espS.data);
      setMqttStatus(mqttS.data);
      setEspDevices(devs.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    Promise.all([getEspSettings(), getEspStatus(), getMqttSettings(), getEspDevices(), getMqttStatus()])
      .then(([s, st, m, d, ms]) => {
        setSettings(s.data); setEspStatus(st.data);
        setMqtt(m.data); setEspDevices(d.data); setMqttStatus(ms.data);
      })
      .catch(() => toast.error('Ayarlar yüklenemedi'))
      .finally(() => setLoading(false));
  }, []);

  usePolling(loadStatus, 5000);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:'Outfit,sans-serif'}}>Ayarlar</h1>
        <p className="text-muted-foreground text-sm mt-1">Sistem ve donanım yapılandırması</p>
      </div>

      <Tabs defaultValue="site">
        <TabsList className="mb-6">
          <TabsTrigger value="site"    className="gap-1.5" data-testid="tab-site"><Monitor size={14} />Site</TabsTrigger>
          <TabsTrigger value="esp"     className="gap-1.5" data-testid="tab-esp"><Cpu size={14} />ESP8266</TabsTrigger>
          <TabsTrigger value="mqtt"    className="gap-1.5" data-testid="tab-mqtt"><Radio size={14} />MQTT</TabsTrigger>
          <TabsTrigger value="security" className="gap-1.5" data-testid="tab-security"><Shield size={14} />Güvenlik</TabsTrigger>
        </TabsList>

        <TabsContent value="site">
          <SiteTab />
        </TabsContent>

        <TabsContent value="esp">
          <EspTab settings={settings} setSettings={setSettings}
            espDevices={espDevices} setEspDevices={setEspDevices} loading={loading} />
        </TabsContent>

        <TabsContent value="mqtt">
          <MqttTab mqtt={mqtt} setMqtt={setMqtt} mqttStatus={mqttStatus} loading={loading} />
        </TabsContent>

        <TabsContent value="security">
          <SecurityTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
